import { inject, provide, reactive, ref, type InjectionKey } from 'vue'
import { getLabelListPage } from '/@/api/system/label'

export interface LabelSelectOption {
  labelCn: string
  labelEn: string
  value: string
}

export type LabelStore = {
  labelOptions: LabelSelectOption[]
  labelLoading: boolean
  labelLoaded: boolean
  loadLabels: () => Promise<void>
  findByEn: (labelEn: string) => LabelSelectOption | undefined
  findByCn: (labelCn: string) => LabelSelectOption | undefined
}

const LABEL_STORE_KEY: InjectionKey<LabelStore> = Symbol('labelStore')

const LABEL_PAGE_SIZE = 500

export function provideLabelStore (): LabelStore {
  const labelOptions = ref<LabelSelectOption[]>([])
  const labelLoading = ref(false)
  const labelLoaded = ref(false)

  async function loadLabels () {
    if (labelLoading.value || labelLoaded.value) return
    labelLoading.value = true
    try {
      const res = await getLabelListPage({
        page: 1,
        size: LABEL_PAGE_SIZE,
      })
      if (res.code === 0 && res.data?.list) {
        labelOptions.value = res.data.list.map((item) => ({
          labelCn: item.labelCn,
          labelEn: item.labelEn,
          value: item.labelEn,
        }))
        labelLoaded.value = true
      } else {
        labelOptions.value = []
      }
    } catch {
      labelOptions.value = []
    } finally {
      labelLoading.value = false
    }
  }

  function findByEn (labelEn: string) {
    return labelOptions.value.find((item) => item.value === labelEn)
  }

  function findByCn (labelCn: string) {
    return labelOptions.value.find((item) => item.labelCn === labelCn)
  }

  const store = reactive({
    get labelOptions () {
      return labelOptions.value
    },
    get labelLoading () {
      return labelLoading.value
    },
    get labelLoaded () {
      return labelLoaded.value
    },
    loadLabels,
    findByEn,
    findByCn,
  }) as LabelStore

  provide(LABEL_STORE_KEY, store)
  loadLabels()

  return store
}

export function useLabelStore (): LabelStore {
  const store = inject(LABEL_STORE_KEY)
  if (!store) {
    throw new Error('useLabelStore must be used inside ImageAnnotator')
  }
  return store
}
